/**
 * Admin: Comp Elite grants
 * ========================
 * Single-screen tool for granting Elite/Elite+ access to influencers and
 * promotional accounts for a set number of days (1–365).
 *
 * Comp grants are paywall-only — they do NOT give admin access.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Gift, Trash2, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../api/client";

export default function AdminCompPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("elite");
  const [days, setDays] = useState(365);
  const [note, setNote] = useState("");
  const [granting, setGranting] = useState(false);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadComps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/admin/comp/list");
      setComps(res.data?.comps || []);
    } catch (err) {
      toast.error("Failed to load comp list");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComps();
  }, [loadComps]);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    try {
      setGranting(true);
      const res = await apiClient.post("/admin/comp/grant", {
        email: email.trim().toLowerCase(),
        tier,
        days: Number(days),
        note: note.trim(),
      });
      toast.success(`${tier === "elite_plus" ? "Elite+" : "Elite"} granted to ${res.data.email} for ${days} days`);
      setEmail("");
      setNote("");
      loadComps();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Grant failed";
      toast.error(detail);
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (emailToRevoke) => {
    if (!window.confirm(`Revoke comp for ${emailToRevoke}? They'll lose Elite access immediately.`)) return;
    try {
      await apiClient.post("/admin/comp/revoke", { email: emailToRevoke });
      toast.success("Comp revoked");
      loadComps();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  const activeCount = comps.filter(c => c.status === "active").length;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 px-4 py-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-50"
          data-testid="comp-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {activeCount} active comp{activeCount === 1 ? "" : "s"}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-rose-600 flex items-center justify-center">
          <Crown className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Comp Elite Grants</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Grant Elite features (no admin access) for marketing & influencer campaigns
          </p>
        </div>
      </div>

      {/* Grant form */}
      <form onSubmit={handleGrant} className="mt-6 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">User email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="influencer@example.com"
              className="form-input"
              data-testid="comp-email-input"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              They must have already signed up at joinunitylove.com so a user record exists.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Tier</label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="form-input"
              data-testid="comp-tier-select"
            >
              <option value="elite">Elite</option>
              <option value="elite_plus">Elite+</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Days (1–365)</label>
            <input
              type="number"
              min="1"
              max="365"
              required
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="form-input"
              data-testid="comp-days-input"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. @theirhandle — Wave 1 influencer"
              className="form-input"
              data-testid="comp-note-input"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={granting}
          className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-rose-600 text-white font-semibold hover:opacity-95 disabled:opacity-50 transition"
          data-testid="comp-grant-btn"
        >
          <Gift className="w-4 h-4" />
          {granting ? "Granting…" : `Grant ${tier === "elite_plus" ? "Elite+" : "Elite"} for ${days} days`}
        </button>
      </form>

      {/* Active comps list */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-3">All Comps</h2>

        {loading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">Loading…</div>
        ) : comps.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            No comp grants yet. Use the form above to grant the first one.
          </div>
        ) : (
          <div className="space-y-2">
            {comps.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                data-testid={`comp-row-${c.email}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    c.status === "active"
                      ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                  }`}>
                    {c.status === "active" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-slate-900 dark:text-slate-50 truncate">
                      {c.display_name || c.email}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {c.email} · {c.tier === "elite_plus" ? "Elite+" : "Elite"} · expires {fmtDate(c.expires_at)}
                      {c.note ? <span> · {c.note}</span> : null}
                    </div>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                  c.status === "active"
                    ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                    : c.status === "expired"
                      ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                      : "bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                }`}>
                  {c.status}
                </span>
                {c.status === "active" && (
                  <button
                    onClick={() => handleRevoke(c.email)}
                    className="p-2 rounded-lg text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition flex-shrink-0"
                    title="Revoke this comp"
                    data-testid={`comp-revoke-${c.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
